import { useMemo, useRef } from "react";
import {
    KalturaPlaylist,
} from "kaltura-typescript-client/api/types";
import Lobby from "../lobby/Lobby";
import { Player } from "@mediaspace/player";
import { HeroProps } from "../HeroProps";
import { CommonHeroLayout } from "../layout/CommonHeroLayout";
import { KalturaPlayer } from "@mediaspace/shared/utils";
import { baseUrl, cdnUrl, translate } from "@mediaspace/shared/utils";
import { Button } from "@kaltura/ds-react-components";
import { ButtonClickAnalyticsType } from "@mediaspace/shared/types/ButtonClickAnalyticsType";

export interface PlaylistHeroProps extends HeroProps {
    playlist: KalturaPlaylist;
}

/**
 * Playlist Hero component - always renders a player
 */
export function PlaylistHero(props: PlaylistHeroProps) {
    const {
        playlist,
        entry,
        categoryId,
        playerConfig,
        onEntryChange,
        canEdit,
    } = props;

    // flag to mark is the initial entry loaded by the player yet?
    // the player loads the first entry in the playlist first
    const entryLoadedRef = useRef(false);

    // player config media
    const media = useMemo(() => {
        return {
            playlistId: playlist.id,
            ks: playerConfig.ks as string,
        };
    }, [playlist, playerConfig]);

    // edit playlist action
    const editAction = {
        name: "edit",
        component: Button,
        props: {
            component: "a",
            href: baseUrl + "/playlist/edit/" + playlist.id + (categoryId ? ('/' + categoryId) : ''),
            children: translate("Edit playlist"),
            analytics: {
                action: "Media Hero - Edit playlist",
                buttonType: ButtonClickAnalyticsType.EDIT,
            },
        },
    };

    // set the relevant action
    const actions = canEdit ? [editAction] : [];

    const switchToEntry = (player: any, entryId: string) => {
        // find the item's index
        const index = player.playlist.items.findIndex(
            (item: any) => item.sources["id"] === entryId
        );

        // move to the player to the relevant entry
        if (index < 0) {
            console.log("requested entry (" + entryId + ") not in playlist");
            return;
        }
        // apparently the player adds one to pretend the index starts from 1..
        player.playlist.playItem(index - 1);
    };

    const handleReady = (player: any) => {
        // is the requested entry not the playlist first entry - switch to it
        const firstEntryId = playlist.playlistContent?.split(",")[0];
        if (firstEntryId !== entry.id) {
            player.addEventListener(
                KalturaPlayer.playlist.PlaylistEventType.PLAYLIST_LOADED,
                () => {
                    switchToEntry(player, entry.id);
                }
            );
        }

        // sync playlist and media page current entry
        player.addEventListener(
            KalturaPlayer.playlist.PlaylistEventType.PLAYLIST_ITEM_CHANGED,
            (e) => {
                // on some cases(live + recorded), e.payload.activeItem.sources.id holds the value of the previous entry
                // before the change. get current entry from player instead
                //const entryId = e.payload.activeItem.sources.id;
                //const entryId = e.target.getMediaInfo().entryId; - unstable
                // temp fix until player fixes e.payload.activeItem.sources.id
                const entryIndex = e.payload.index;
                const entryId =
                    player.playlist._mediaInfoList[entryIndex].entryId;

                // the player fires PLAYLIST_ITEM_CHANGED for the first entry. if we
                // requested a different initial entry - we will load the wrong entry in the media page.
                // so we ignore all events untill the entry we requested is loaded.
                if (entryLoadedRef.current) {
                    //update page url to include the current playing entry id
                    window.history.pushState?.(null, "Playlist page", baseUrl + `/playlist/dedicated/` + (categoryId ? `${categoryId}/` : '') +`${playlist.id}/${entryId}`);

                    // inform the media page
                    onEntryChange?.(entryId);
                }

                // the initial entry we want is loaded
                if (!entryLoadedRef.current && entryId === entry.id) {
                    // update entry loaded flag
                    entryLoadedRef.current = true;
                }
            }
        );
    };

    return (
        <CommonHeroLayout>
            {entry && (
                <Player
                    media={media}
                    config={playerConfig}
                    className="kms-ds-media-page-player"
                    onReady={handleReady}
                />
            )}
            {!entry && (
                <Lobby
                    thumbnailUrl={cdnUrl + "/media/Thumbnail.png"}
                    statusLabel={translate("no content")}
                    title={playlist!.name}
                    actions={actions}
                />
            )}
        </CommonHeroLayout>
    );
}

export default PlaylistHero;
